
import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthUser, UserRole } from '../types';
import { api } from '../services/api';

interface LoginResult {
  success: boolean;
  message?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  login: (username: string, password: string, districtId?: string) => Promise<LoginResult>;
  logout: () => void;
  updateUser: (newUserData: Partial<AuthUser>) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const storedUser = localStorage.getItem('hrms_user');
    return storedUser ? JSON.parse(storedUser) : null;
  });


  const login = async (username: string, password: string, districtId?: string): Promise<LoginResult> => {
    try {
      const response = await api.login({ username, password, hqId: districtId });

      if (response.success && response.user) {
        const userData: AuthUser = {
          id: response.user.id,
          username: response.user.username,
          fullName: response.user.full_name,
          role: response.user.role as UserRole,
          avatar: api.getPhotoUrl(response.user.avatar) || `https://ui-avatars.com/api/?name=${encodeURIComponent(response.user.full_name)}&background=random`,
          hqId: response.user.hqId,
          hqTitle: response.user.hqTitle
        };
        setUser(userData);
        localStorage.setItem('hrms_user', JSON.stringify(userData));
        return { success: true };
      }

      return {
        success: false,
        message: response.message || 'Invalid credentials'
      };
    } catch (error) {
      console.error("Login Error", error);
      return {
        success: false,
        message: 'Connection failed. Ensure XAMPP (Apache/MySQL) is running and the URL in api.ts is correct.'
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('hrms_user');
  };

  const updateUser = (newUserData: Partial<AuthUser>) => {
    if (user) {
      const updatedUser = { ...user, ...newUserData };
      setUser(updatedUser);
      localStorage.setItem('hrms_user', JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
